import React, { useContext, useEffect, useMemo, useState } from 'react';
import axios, { AxiosError } from 'axios';
import { Icon, Layout } from 'components';
import { Route } from 'react-router-dom';
import styled from 'styled-components';
import { useMixpanel } from 'hooks';
import { MixpanelPage } from 'hooks/useMixpanel/events';
import {
  ListingFilterProvider,
  RegionDataProvider,
  MarketAnalytics,
  AnalyticsAPI,
} from '@bb/web-components.analytics';
import errorReporter from '@app/errorReporter';
import { AuthContext, AuthContextType, ListingLabelContext } from 'contexts';
import { featureEnabled } from 'utils';
import { OAuthController } from 'api';
import { memoize } from 'lodash';
import { SupraLogo } from 'components/SideMenu/StyledComponents';
import Loading from '../../../app/react-common/Loading';
import logo from '../../../img/logo_v2.svg';
import { adminsHomePage, agentsHomePage } from '../../constants';

type AnalyticsPageType = {
  pageType?: 'dashboard' | 'market';
};

const StyledFlexColumn = styled(Layout.Content)`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 72px 24px;
  width: 100vw;

  .brokerbay-logo {
    width: 100%;
    padding: 32px;
    background-image: url(${logo});
    background-size: 75%;
    background-repeat: no-repeat;
    background-position: 50%;
    transition: all 0.2s;
  }

  h3 {
    margin-top: 48px;
    margin-bottom: 24px;
    font-weight: 400;
    font-size: 20px;
    line-height: 28px;
    color: black;
    text-align: center;
  }
`;

const refreshAccessToken = async () => {
  try {
    const accessToken = await OAuthController.refreshToken();
    return accessToken;
  } catch (err) {
    errorReporter.error(err);
    return null;
  }
};

// use single instance to allow caching
const getAnalyticsApiInstance: (
  analyticsUrl: string,
) => ReturnType<typeof AnalyticsAPI> = (() => {
  // this will return a new instance if and only if analyticsUrl changes
  const memoizedInstanceGetter = memoize(AnalyticsAPI);
  return (analyticsUrl: string) =>
    memoizedInstanceGetter(analyticsUrl, refreshAccessToken);
})();

const pageToMixpanelEventMap: Record<
  NonNullable<AnalyticsPageType['pageType']>,
  MixpanelPage
> = {
  dashboard: MixpanelPage.AnalyticsMyBrokerage,
  market: MixpanelPage.AnalyticsMarketTrends,
};

const AnalyticsPage: React.FC<AnalyticsPageType> = ({
  pageType = 'market',
}) => {
  const { auth } = useContext<AuthContextType>(AuthContext);
  const { getLabel, isHidden } = useContext(ListingLabelContext);
  const [analyticsUrl, setAnalyticsUrl] = useState<string>();
  const [analyticsOrg, setAnalyticsOrg] = useState();
  const [analyticsTeam, setAnalyticsTeam] = useState();
  const [propertyTypes, setPropertyTypes] = useState<string[]>();
  const [propertyTypesV2, setPropertyTypesV2] =
    useState<Record<string, (string | null)[]>>();
  const [API, setAPI] = useState<ReturnType<typeof AnalyticsAPI>>();

  useMixpanel({
    auth,
    pageTracking: { pageName: pageToMixpanelEventMap[pageType] },
  });

  const dataModalRedirectUrl = useMemo(() => {
    const root = `${window.location.origin}/#`;
    if (auth.role === 'AGENT') return root + agentsHomePage;
    return root + adminsHomePage;
  }, [auth]);

  const fetchAnalyticsUrl = async () => {
    try {
      const [
        {
          data: { analyticsUrl: fetchedUrl },
        },
        accessToken,
      ] = await Promise.all([
        axios.get<{ analyticsUrl: string }>('/secrets/analyticsUrl'),
        refreshAccessToken(),
      ]);

      if (accessToken) {
        window.localStorage.setItem('bbAnalytics:accessToken', accessToken);
      } else {
        window.localStorage.removeItem('bbAnalytics:accessToken');
      }

      if (auth?.assumingUserId) {
        window.localStorage.setItem('bbAnalytics:assumeUserId', auth.id);
      } else {
        window.localStorage.removeItem('bbAnalytics:assumeUserId');
      }

      setAnalyticsUrl(fetchedUrl);
      const APIObj = getAnalyticsApiInstance(fetchedUrl);
      setAPI(APIObj);

      if (featureEnabled('analyticsEnhanceFeature')) {
        const [org, team] = await Promise.all([
          APIObj.getOrg(),
          APIObj.getOrgTeams(),
        ]);
        setAnalyticsOrg(org);
        setAnalyticsTeam(team);
      }
      const result = await APIObj.getPropertyTypes();
      if (
        (
          result as {
            err: AxiosError<any>;
          }
        ).err
      )
        return;

      const { propTypes, propTypesV2 } = result as {
        propTypes?: string[] | undefined;
        propTypesV2?: Record<string, string[]> | undefined;
      };
      // setPropertyTypesV2 must be called before setPropertyTypes,
      // since there is no separate loading check yet, but just checking for
      // propertyTypes existence.
      if (propTypesV2) {
        setPropertyTypesV2(propTypesV2);
      }
      setPropertyTypes(
        Array.isArray(propTypes) && propTypes.length ? propTypes : [],
      );
    } catch (err) {
      errorReporter.error(err);
    }
  };

  useEffect(() => {
    fetchAnalyticsUrl();
  }, []);

  if (!auth || !analyticsUrl || !API) return <Loading size="fa-5x" />;
  if (
    featureEnabled('analyticsLegacyTurnOffFeature') &&
    auth?.org?.onboardingPortalId &&
    (process.env.TABLEAU_ANALYTICS_PORTAL_ID?.split(',') || []).includes(
      auth.org.onboardingPortalId.toString(),
    )
  ) {
    return null;
  }
  if (
    (featureEnabled('analyticsEnhanceFeature') &&
      (!analyticsOrg || !analyticsTeam)) ||
    !propertyTypes
  )
    return <Loading size="fa-5x" />;

  const parsedOrg = auth.org;
  const parsedAuth = {
    ...auth,
    team: auth.team,
    isBrokerBayAdmin: false,
    org: auth.org.id,
  };

  if (/Mobi|Android/i.test(navigator.userAgent)) {
    return (
      <StyledFlexColumn>
        <SupraLogo />
        <h3>You must be on a desktop computer to view BrokerBay Analytics.</h3>
        <Icon type="exclamation" color="primary" width="25vw" />
      </StyledFlexColumn>
    );
  }

  return (
    <Route
      path={`/analytics/${pageType}`}
      render={() => (
        <ListingFilterProvider
          // @ts-ignore
          org={analyticsOrg || parsedOrg}
          // @ts-ignore
          user={parsedAuth}
          teams={analyticsTeam || []}
          pageType={pageType}
          analyticsUrl={analyticsUrl}
          API={API}
          hasAnalyticsEnhanceFeature={featureEnabled('analyticsEnhanceFeature')}
          hasAnalyticsRevampP1Feature={featureEnabled(
            'analyticsRevampP1Feature',
          )}
          hasAnalyticsRevampP2Feature={featureEnabled(
            'analyticsRevampP2Feature',
          )}
          allPropertyTypes={propertyTypes?.length ? propertyTypes : undefined}
          allPropertyTypesV2={propertyTypesV2}
          getOverride={getLabel}
          getOverrideHidden={isHidden}
        >
          <RegionDataProvider API={API}>
            <FontWrapper>
              <MarketAnalytics
                pageType={pageType}
                useDataModal
                dataModalRedirectUrl={dataModalRedirectUrl}
              />
            </FontWrapper>
          </RegionDataProvider>
        </ListingFilterProvider>
      )}
    />
  );
};

export default AnalyticsPage;

const FontWrapper = styled.div`
  margin-top: 20px;

  .main-layout {
    padding: 22px 20px 20px 24px !important;
    width: 100% !important;
  }

  * {
    font-family: 'Inter', sans-serif !important;
  }
`;
